import { Board } from "./Board";
import { Button } from "./ui/Button";
import { isOverdue } from "./TaskCard";
import { mindmapUrl, openLink, safeUrl } from "../lib/links";
import type { Project, Status, Task } from "../types";

interface Props {
  project: Project;
  tasks: Task[];
  onOpenTask: (task: Task) => void;
  onMove: (task: Task, status: Status) => void;
  onArchive: () => void;
  onRestore: () => void;
  onEdit: () => void;
  onMindmap: () => void;
  onHome: () => void;
}

export function ProjectView({
  project,
  tasks,
  onOpenTask,
  onMove,
  onArchive,
  onRestore,
  onEdit,
  onMindmap,
  onHome,
}: Props) {
  const done = tasks.filter((t) => t.status === "Done").length;
  const late = tasks.filter(isOverdue).length;

  return (
    <>
      <div className="phead">
        <div>
          <div className="phead__name">
            <span className="pcard__swatch" style={{ background: project.color }} />
            {project.name}
          </div>
          <div className="phead__meta">
            {tasks.length - done} open &middot; {done} done
            {late > 0 && (
              <>
                {" "}
                &middot; <span className="pcard__late">{late} overdue</span>
              </>
            )}
            {project.archived && " · Archived, read only"}
          </div>
        </div>
        <div className="phead__acts">
          <LinkButton
            label="Task file"
            href={project.taskFile}
            missing="No task file link set. Add one under Edit project."
          />
          <LinkButton
            label="Project drive"
            href={project.driveFolder}
            missing="No project drive link set. Add one under Edit project."
          />
          <Button
            onClick={onMindmap}
            disabled={!mindmapUrl(project.mindmap)}
            title={
              mindmapUrl(project.mindmap) ??
              (project.mindmap
                ? `That mindmap value is not usable: ${project.mindmap}`
                : "No mindmap set. Add a link or filename under Edit project.")
            }
          >
            Open mindmap
          </Button>
          <Button onClick={onEdit}>Edit project</Button>
          {project.archived ? (
            <Button onClick={onRestore}>Restore</Button>
          ) : (
            <Button onClick={onArchive}>Archive</Button>
          )}
          <Button variant="quiet" onClick={onHome}>
            All projects
          </Button>
        </div>
      </div>

      {project.archived && (
        <div className="banner">
          This project is archived. Tasks are read only until you restore it.
        </div>
      )}

      <Board
        tasks={tasks}
        project={project}
        readOnly={project.archived}
        onOpen={onOpenTask}
        onMove={onMove}
      />
    </>
  );
}

/**
 * A header button that opens a project link in a new tab.
 *
 * Disabled rather than hidden when unset, so the project header always shows
 * the same three slots and it is obvious what is missing. The link is vetted
 * before it is opened: these values come out of a shared spreadsheet.
 */
function LinkButton({
  label,
  href,
  missing,
}: {
  label: string;
  href: string;
  missing: string;
}) {
  const url = safeUrl(href);
  const unusable = href.trim() !== "" && url === null;
  return (
    <Button
      disabled={!url}
      title={unusable ? `That link is not a valid web address: ${href}` : url ?? missing}
      onClick={() => openLink(href)}
    >
      {label}
    </Button>
  );
}
